import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'backups',
  slug: 'backups',
  name: 'Nexus Backups',
  description: 'Backup and disaster-recovery tracking: the generic per-vendor BCDR job tracker, the built-in Microsoft 365 -> Wasabi backup engine, and Suite tenant-data DR snapshots.',
  icon: 'CloudSync',
  endpoints: [
    {
      slug: 'list-jobs',
      method: 'GET',
      path: '/api/backups',
      summary: 'List backup jobs',
      description: 'Returns every tracked backup job for the current tenant, manual or vendor-synced, each annotated with a live state (healthy, failed, stale, unknown, disabled) and age since last successful run.',
      auth: 'session',
      responseExample: `[
  {
    "id": "bkp_4a91",
    "name": "SIS database",
    "source": "PowerSchool export",
    "type": "Cloud",
    "schedule": "daily",
    "windowHours": 36,
    "location": "Azure Blob - use1",
    "vendor": "",
    "notes": "",
    "enabled": true,
    "lastRun": "2026-07-20T03:12:00.000Z",
    "lastStatus": "success",
    "sizeGB": 4.2,
    "createdAt": "2026-04-02T15:00:00.000Z",
    "updatedAt": "2026-07-20T03:12:00.000Z",
    "state": "healthy",
    "ageHours": 6.1
  }
]`,
      sourceFile: 'routes/backups.js',
    },
    {
      slug: 'create-job',
      method: 'POST',
      path: '/api/backups',
      summary: 'Create a tracked backup job',
      description: 'Registers a new backup job to monitor. Used for jobs a vendor integration doesn\'t cover - the job starts with no run history until a report comes in or a vendor sync matches it by externalRef.',
      auth: 'session',
      requestBodyExample: `{
  "name": "File server nightly",
  "source": "Veeam - FS01",
  "type": "Local + Cloud",
  "schedule": "daily",
  "windowHours": 36,
  "location": "Wasabi - district-backups",
  "vendor": "Veeam",
  "notes": "Excludes the archive share."
}`,
      responseExample: `{
  "id": "bkp_7c03",
  "name": "File server nightly",
  "source": "Veeam - FS01",
  "type": "Local + Cloud",
  "schedule": "daily",
  "windowHours": 36,
  "location": "Wasabi - district-backups",
  "vendor": "Veeam",
  "notes": "Excludes the archive share.",
  "enabled": true,
  "lastRun": "",
  "lastStatus": "",
  "sizeGB": 0,
  "createdAt": "2026-07-20T14:00:00.000Z",
  "updatedAt": "2026-07-20T14:00:00.000Z",
  "state": "unknown",
  "ageHours": null
}`,
      sourceFile: 'routes/backups.js',
    },
    {
      slug: 'report-job-result',
      method: 'POST',
      path: '/api/backups/:id/report',
      summary: 'Report a backup job result',
      description: 'Records the outcome of a run for a tracked job - called manually from the dashboard or by an external backup tool/cron. A failed report raises a high-severity Backup alert (deduplicated per job).',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Backup job id.' }],
      requestBodyExample: `{ "status": "success", "at": "2026-07-20T03:12:00.000Z", "sizeGB": 4.4 }`,
      responseExample: `{
  "id": "bkp_4a91",
  "name": "SIS database",
  "lastRun": "2026-07-20T03:12:00.000Z",
  "lastStatus": "success",
  "sizeGB": 4.4,
  "state": "healthy",
  "ageHours": 0
}`,
      sourceFile: 'routes/backups.js',
    },
    {
      slug: 'cross-vendor-dashboard',
      method: 'GET',
      path: '/api/backups/dashboard',
      summary: 'Get the cross-vendor backup dashboard',
      description: 'Returns a live per-vendor and overall health rollup computed from the current job list, plus a trend of past rollups captured on every scan (manual "Run scan" or the 12-hour scheduled cadence).',
      auth: 'session',
      params: [{ name: 'limit', in: 'query', type: 'number', description: 'Max trend snapshots to return, newest first. Default 30, max 200.' }],
      responseExample: `{
  "byVendor": [
    { "vendor": "veeam", "vendorLabel": "Veeam", "configured": true, "healthy": 3, "failed": 0, "stale": 1, "unknown": 0 }
  ],
  "overall": { "total": 12, "healthy": 9, "failed": 1, "stale": 2, "unknown": 0 },
  "trend": [
    { "id": "snap_91a2", "at": "2026-07-20T00:00:00.000Z", "byVendor": [], "overall": { "total": 12, "healthy": 9, "failed": 1, "stale": 2, "unknown": 0 } }
  ]
}`,
      sourceFile: 'routes/backups.js, lib/backup-dashboard.js',
    },
    {
      slug: 'm365wasabi-get-settings',
      method: 'GET',
      path: '/api/m365wasabi/settings',
      summary: 'Get Microsoft 365 -> Wasabi backup settings',
      description: 'Returns the current tenant\'s M365-to-Wasabi backup configuration (workloads, target bucket, schedule, retention). Secret fields are never echoed - only a hasWasabiKeys boolean indicates whether credentials are set.',
      auth: 'session',
      responseExample: `{
  "enabled": true,
  "workloads": ["mail", "onedrive", "sharepoint"],
  "wasabiBucket": "district-m365-backups",
  "wasabiEndpoint": "https://s3.wasabisys.com",
  "wasabiRegion": "us-east-1",
  "wasabiKeyPrefix": "m365-backups",
  "hasWasabiKeys": true,
  "graphConfigured": true,
  "frequency": "daily",
  "hour": 2,
  "dayOfWeek": 1,
  "retentionRuns": 7,
  "retentionDays": 30,
  "retentionDryRun": false,
  "userFilter": "",
  "siteFilter": "",
  "lastRunAt": "2026-07-20T02:00:11.000Z",
  "lastRunDay": "2026-07-20"
}`,
      sourceFile: 'routes/m365wasabi.js, lib/m365wasabi.js',
    },
    {
      slug: 'm365wasabi-run-now',
      method: 'POST',
      path: '/api/m365wasabi/run-now',
      summary: 'Run an M365 -> Wasabi backup now',
      description: 'Triggers an on-demand backup of the configured Microsoft 365 workloads to Wasabi, outside the hourly cadence tick. Uploads each item, writes a manifest, and records a run entry.',
      auth: 'session',
      requestBodyExample: `{ "workloads": ["mail", "onedrive"] }`,
      responseExample: `{
  "ok": true,
  "run": {
    "id": "run_2f10",
    "runId": "2026-07-20T14-05-00Z",
    "startedAt": "2026-07-20T14:05:00.000Z",
    "finishedAt": "2026-07-20T14:11:32.000Z",
    "workloads": ["mail", "onedrive"],
    "counts": { "itemsOk": 842, "itemsFailed": 0, "bytesUploaded": 1288490188 },
    "status": "ok",
    "manifestKey": "m365-backups/internal/2026-07-20T14-05-00Z/manifest.json"
  }
}`,
      sourceFile: 'routes/m365wasabi.js, lib/m365wasabi-engine.js',
    },
    {
      slug: 'tenantbackup-list-runs',
      method: 'GET',
      path: '/api/tenantbackup/runs',
      summary: 'List Suite tenant-data backup runs',
      description: 'Returns this tenant\'s Suite data disaster-recovery snapshot history (the tenant\'s own operational store, not Microsoft 365), newest first. Each run captures one point-in-time snapshot to the configured S3-compatible bucket.',
      auth: 'session',
      params: [{ name: 'limit', in: 'query', type: 'number', description: 'Max runs to return. Default 50, max 200.' }],
      responseExample: `[
  {
    "id": "run_88b1",
    "runId": "2026-07-20T03-00-00Z",
    "startedAt": "2026-07-20T03:00:00.000Z",
    "finishedAt": "2026-07-20T03:02:41.000Z",
    "status": "ok",
    "manifestKey": "suite-data-backups/internal/2026-07-20T03-00-00Z/manifest.json",
    "snapshotKey": "suite-data-backups/internal/2026-07-20T03-00-00Z/snapshot.json"
  }
]`,
      sourceFile: 'routes/tenantbackup.js, lib/tenantbackup-engine.js',
    },
    {
      slug: 'tenantbackup-prune',
      method: 'POST',
      path: '/api/tenantbackup/prune',
      summary: 'Prune old Suite tenant-data backup runs',
      description: 'Applies the configured retention policy (retentionRuns / retentionDays) to past successful snapshots, deleting the aged-out ones from storage. Only "ok" runs are ever pruning candidates.',
      auth: 'session',
      requestBodyExample: `{ "dryRun": true }`,
      responseExample: `{
  "dryRun": true,
  "candidates": ["2026-06-10T03-00-00Z", "2026-06-11T03-00-00Z"],
  "pruned": []
}`,
      sourceFile: 'routes/tenantbackup.js, lib/tenantbackup-engine.js',
    },
  ],
};
